import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Lock, Unlock, ShieldOff } from 'lucide-react';

interface EncryptionSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetId: string;
    targetType: 'channel' | 'topic';
    currentMode: 'optional' | 'forced' | 'disabled';
}

export default function EncryptionSettingsModal({ isOpen, onClose, targetId, targetType, currentMode }: EncryptionSettingsModalProps) {
    const { updateChannel, updateTopic, hasPermission } = useApp();
    const [mode, setMode] = useState<'optional' | 'forced' | 'disabled'>(currentMode);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isAdmin = hasPermission('admin:view') || hasPermission('super:admin');

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (targetType === 'channel') {
                await updateChannel(targetId, { encryption_mode: mode });
            } else {
                await updateTopic(targetId, { encryption_mode: mode });
            }
            onClose();
        } catch (error) {
            console.error('Failed to update encryption settings:', error);
        }
        setIsSubmitting(false);
    };

    const options = [
        {
            value: 'optional',
            label: 'Optional Encryption',
            description: 'Members can choose to encrypt individual messages.',
            icon: <Unlock size={18} className="text-muted" />
        },
        {
            value: 'forced',
            label: 'Forced Encryption',
            description: 'All messages sent in this group will be encrypted automatically.',
            icon: <Lock size={18} className="text-primary" />
        },
        {
            value: 'disabled',
            label: 'Disabled',
            description: 'Encryption is not allowed. All messages will be plaintext.',
            icon: <ShieldOff size={18} className="text-danger" />
        }
    ].filter(opt => opt.value !== 'disabled' || isAdmin || currentMode === 'disabled');

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <h2 className="modal-title">Encryption Settings</h2>
                    <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
                </header>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 16 }}>
                            Control how End-to-End Encryption works for this {targetType}.
                            Changing this will apply to all future messages.
                        </p>

                        <div className="encryption-options" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {options.map(opt => (
                                <label
                                    key={opt.value}
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        padding: 12,
                                        borderRadius: 'var(--radius-md)',
                                        border: `1px solid ${mode === opt.value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                                        background: mode === opt.value ? 'rgba(33, 150, 243, 0.05)' : 'transparent',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="encryption_mode"
                                        value={opt.value}
                                        checked={mode === opt.value}
                                        onChange={(e) => setMode(e.target.value as any)}
                                        style={{ marginTop: 4 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            {opt.icon}
                                            <div style={{ fontWeight: 500, fontSize: 'var(--font-size-md)' }}>{opt.label}</div>
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                                            {opt.description}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting || mode === currentMode}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
